import { create } from 'zustand';
import { INITIAL_FORM_DATA, API_URL, TIMEZONE_ALIASES } from '../constants/dashboard';
import { getStoredUser, saveStoredUser, mergeStoredUser } from '../auth/userStorage';
import { authHeaders, handleAuthFailure, SESSION_RESET_EVENT } from '../api/client';

const deriveConnectedAccounts = (user) => ({
    telegram: !!user.telegramConnected,
    google: !!(user.linkedAccounts && user.linkedAccounts.includes('google')),
    linkedin: !!(user.linkedAccounts && user.linkedAccounts.includes('linkedin')),
});

// Replace-style setter that also accepts an updater fn (prev => next),
// mirroring React's setState. A bare `set({ [key]: data })` would store the
// updater FUNCTION itself when a caller passes `prev => ...` — that once turned
// formData into a function and blanked the whole profile (next-week toggle).
// Routing every simple setter through here kills that footgun class-wide.
const replaceSetter = (set, key) => (data) =>
    set(state => ({ [key]: typeof data === 'function' ? data(state[key]) : data }));

const useDashboardStore = create((set, get) => ({
    // --- Profile Form Data ---
    formData: { ...INITIAL_FORM_DATA },
    initialFormData: null,
    isEditMode: false,

    // --- Reference Data ---
    countries: [],
    cities: [],
    interests: { professional: [], personal: [], categories: {} },
    communities: [],
    currentMatch: null,

    // --- User ---
    user: getStoredUser() || { username: '', email: null },
    connectedAccounts: { telegram: false, google: false, linkedin: false },
    matchingContext: 'global',

    // --- UI State ---
    isLoading: false,
    isSaving: false,
    message: { type: '', text: '' },
    imageError: false,
    savedSections: {},
    showSaveIndicator: false,

    // --- Modals ---
    modals: {
        language: false,
        timezone: false,
        days: false,
        country: false,
        city: false,
        professionalInterests: false,
        personalInterests: false,
        googleLink: false,
        telegramConnect: false,
        disconnectTelegram: false,
        mergeConfirm: false,
        success: false,
        error: false,
        confirm: false,
        deleteAccount: false,
    },
    successMessage: '',
    errorMessage: '',

    // --- Telegram Connect Flow ---
    telegramConnect: {
        username: '',
        otp: '',
        error: '',
        step: 1,
        codeFromUrl: false,
    },
    pendingMergeProfile: null,

    // --- Profile Completion ---
    showCompletionSuccess: false,
    wasIncomplete: false,

    // --- Search/Filter (local to modals) ---
    countrySearch: '',
    newCityName: '',

    // ===================== ACTIONS =====================

    // --- Form Actions ---
    setFormField: (field, value) => set(state => ({
        formData: { ...state.formData, [field]: value }
    })),

    // Object OR updater fn — see replaceSetter. The next-week toggle passes
    // `prev => ...`; a plain replace setter stored the fn and blanked the profile.
    setFormData: replaceSetter(set, 'formData'),

    setInitialFormData: replaceSetter(set, 'initialFormData'),

    toggleEditMode: () => set(state => ({ isEditMode: !state.isEditMode })),

    setIsEditMode: replaceSetter(set, 'isEditMode'),

    // --- Multi-select toggle ---
    toggleMultiSelect: (fieldName, value) => {
        const state = get();
        const current = state.formData[fieldName] || [];
        const newValues = current.includes(value)
            ? current.filter(item => item !== value)
            : [...current, value];
        const updatedData = { ...state.formData, [fieldName]: newValues };
        set({ formData: updatedData });
        state.autoSaveProfile(updatedData, fieldName);
    },

    removeOtherInterest: (field, interestToRemove) => {
        const state = get();
        const currentString = state.formData[field] || '';
        const items = currentString.split(/[,.;]+/).map(s => s.trim()).filter(s => s.length > 0);
        const newItems = items.filter(item => item !== interestToRemove);
        const newString = newItems.join(', ');
        const updatedData = { ...state.formData, [field]: newString };
        set({ formData: updatedData });
        state.autoSaveProfile(updatedData, field);
    },

    // --- Reference Data ---
    setCountries: replaceSetter(set, 'countries'),
    setCities: replaceSetter(set, 'cities'),
    setInterests: replaceSetter(set, 'interests'),
    setCommunities: replaceSetter(set, 'communities'),
    setCurrentMatch: replaceSetter(set, 'currentMatch'),

    // --- User ---
    setUser: (user) => {
        saveStoredUser(user);
        set({ user, connectedAccounts: deriveConnectedAccounts(user) });
    },

    // Merge updates over the FRESH localStorage user (never over the possibly
    // stale in-memory copy) — spreading get().user over storage caused the
    // session clobber incidents.
    mergeUser: (updates) => {
        const updatedUser = mergeStoredUser(updates);
        set({ user: updatedUser, connectedAccounts: deriveConnectedAccounts(updatedUser) });
        return updatedUser;
    },
    setConnectedAccounts: replaceSetter(set, 'connectedAccounts'),
    setMatchingContext: replaceSetter(set, 'matchingContext'),

    // --- UI State ---
    setIsLoading: replaceSetter(set, 'isLoading'),
    setMessage: replaceSetter(set, 'message'),
    setImageError: replaceSetter(set, 'imageError'),
    setShowSaveIndicator: replaceSetter(set, 'showSaveIndicator'),
    setIsSaving: replaceSetter(set, 'isSaving'),

    showSavedIndicator: (section) => {
        set(state => ({
            savedSections: { ...state.savedSections, [section]: true }
        }));
        setTimeout(() => {
            set(state => ({
                savedSections: { ...state.savedSections, [section]: false }
            }));
        }, 3000);
    },

    // --- Modals ---
    openModal: (name) => set(state => ({
        modals: { ...state.modals, [name]: true }
    })),

    closeModal: (name) => set(state => ({
        modals: { ...state.modals, [name]: false }
    })),

    setSuccessMessage: replaceSetter(set, 'successMessage'),
    setErrorMessage: replaceSetter(set, 'errorMessage'),

    // --- Telegram Connect ---
    setTelegramConnect: (updates) => set(state => ({
        telegramConnect: { ...state.telegramConnect, ...updates }
    })),

    resetTelegramConnect: () => set({
        telegramConnect: { username: '', otp: '', error: '', step: 1, codeFromUrl: false }
    }),

    setPendingMergeProfile: replaceSetter(set, 'pendingMergeProfile'),

    // --- Profile Completion ---
    setShowCompletionSuccess: replaceSetter(set, 'showCompletionSuccess'),
    setWasIncomplete: replaceSetter(set, 'wasIncomplete'),

    // --- Search ---
    setCountrySearch: replaceSetter(set, 'countrySearch'),
    setNewCityName: replaceSetter(set, 'newCityName'),

    // ===================== ASYNC ACTIONS =====================

    autoSaveProfile: async (updatedData, section) => {
        if (!updatedData || !updatedData.name || !updatedData.family) return;

        if (!getStoredUser()) return;

        try {
            set({ isSaving: true, showSaveIndicator: true });

            // Target is the session token's subject — the backend ignores any
            // identifiers in the body. authHeaders reads localStorage fresh so a
            // stale in-memory user can never strip the token.
            const response = await fetch(`${API_URL}/api/profile`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders('user') },
                body: JSON.stringify({ profile: updatedData }),
            });

            if (handleAuthFailure(response)) return;

            const data = await response.json();

            if (data.success) {
                set({ initialFormData: updatedData, isSaving: false });
                get().showSavedIndicator(section);
                setTimeout(() => set({ showSaveIndicator: false }), 2000);
            } else {
                set({ isSaving: false, showSaveIndicator: false });
            }
        } catch (error) {
            set({ isSaving: false, showSaveIndicator: false });
        }
    },

    fetchInitialData: async () => {
        const storedUser = localStorage.getItem('user');
        const fetchPromises = [
            fetch(`${API_URL}/api/countries`),
            fetch(`${API_URL}/api/interests`)
        ];

        if (storedUser) {
            const user = JSON.parse(storedUser);
            // Ensure store has full user object from localStorage
            if (!get().user) set({ user });
            set({ isLoading: true });
            // No identifier in the URL: the backend serves the session token's
            // own profile. This is what retired the stale-username retry — a
            // changed Telegram handle can no longer break the lookup, because
            // nothing looks the user up by handle any more.
            fetchPromises.push(fetch(`${API_URL}/api/profile`, { headers: authHeaders() }));
        }

        try {
            const responses = await Promise.all(fetchPromises);
            const parsePromises = responses.map(r => r.json());
            const [countriesData, interestsData, profileData] = await Promise.all(parsePromises);

            if (countriesData.success) set({ countries: countriesData.countries });
            if (interestsData.success) set({ interests: interestsData.interests });

            if (profileData && profileData.success) {
                const currentFormData = get().formData;
                const mappedTimezone = profileData.profile.timezone
                    ? (TIMEZONE_ALIASES[profileData.profile.timezone] || profileData.profile.timezone)
                    : currentFormData.timezone;

                const profileFormData = {
                    ...currentFormData,
                    ...profileData.profile,
                    timezone: mappedTimezone
                };

                set({
                    formData: profileFormData,
                    initialFormData: profileFormData,
                });

                // Sync critical fields from profile to user store
                const userUpdates = {};
                if (profileData.profile.email) userUpdates.email = profileData.profile.email;
                if (profileData.profile.tg_username) {
                    userUpdates.username = profileData.profile.tg_username;
                    userUpdates.Tg_Username = profileData.profile.tg_username;
                    userUpdates.telegramConnected = true;
                }
                if (Object.keys(userUpdates).length > 0) {
                    get().mergeUser(userUpdates);
                }

                const isProfileComplete = profileFormData.name?.trim() && profileFormData.family?.trim();
                if (!isProfileComplete) set({ isEditMode: true });

                if (profileData.currentMatch) set({ currentMatch: profileData.currentMatch });
            }
        } catch (error) {
            // silent fail - data not loaded
        } finally {
            set({ isLoading: false });
        }
    },

    fetchCommunities: async () => {
        if (!getStoredUser()?.token) return;

        try {
            const response = await fetch(`${API_URL}/api/my/communities`, { headers: authHeaders() });
            const data = await response.json();
            if (response.ok) {
                set({
                    communities: data.communities || [],
                    matchingContext: data.matchingContext || 'global'
                });
            }
        } catch (err) {
            // silent fail
        }
    },

    fetchCities: async () => {
        const { formData } = get();
        if (!formData.country || !formData.country.iso) {
            set({ cities: [] });
            return;
        }
        try {
            const response = await fetch(`${API_URL}/api/cities?countryIso=${formData.country.iso}`);
            const data = await response.json();
            if (data.success) set({ cities: data.cities });
        } catch (error) {
            // silent fail
        }
    },

    handleMatchingContextChange: async (newContext) => {
        try {
            const response = await fetch(`${API_URL}/api/my/matching-context`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ matchingContext: newContext }),
            });
            if (response.ok) {
                set({ matchingContext: newContext });
                get().showSavedIndicator('matchingContext');
            }
        } catch (err) {
            // silent fail
        }
    },

    handleAvatarChange: async (file) => {
        if (!file) return;
        const storedUser = localStorage.getItem('user');
        if (!storedUser) return;
        const user = JSON.parse(storedUser);

        const formDataUpload = new FormData();
        formDataUpload.append('avatar', file);
        formDataUpload.append('username', user.username);

        if (file.size > 5 * 1024 * 1024) {
            set({ message: { type: 'error', text: 'The photo is too large. Max size is 5MB.' } });
            return;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 90000);

        try {
            set({ isLoading: true });
            const response = await fetch(`${API_URL}/api/upload-avatar`, {
                method: 'POST',
                headers: authHeaders('user'),
                body: formDataUpload,
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            if (handleAuthFailure(response)) return;
            const data = await response.json();

            if (data.success) {
                set(state => ({
                    formData: { ...state.formData, avatar: data.avatarUrl },
                    initialFormData: { ...state.initialFormData, avatar: data.avatarUrl },
                    message: { type: 'success', text: 'Avatar updated successfully!' }
                }));
            } else {
                set({ message: { type: 'error', text: data.message || 'Failed to upload avatar' } });
            }
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                set({ message: { type: 'error', text: 'Upload timed out. Please try with a smaller image.' } });
            } else {
                set({ message: { type: 'error', text: 'An error occurred while uploading' } });
            }
        } finally {
            set({ isLoading: false });
        }
    },

    handleAddCity: async () => {
        const { newCityName, formData } = get();
        if (!newCityName.trim() || !formData.country) return;

        try {
            const response = await fetch(`${API_URL}/api/cities`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newCityName,
                    countryIso: formData.country.iso
                }),
            });
            const data = await response.json();
            if (data.success) {
                const newData = { ...formData, city: data.city };
                set(state => ({
                    cities: [...state.cities, data.city],
                    formData: newData,
                    newCityName: '',
                }));
                get().closeModal('city');
                get().autoSaveProfile(newData, 'city');
            }
        } catch (error) {
            // silent fail
        }
    },

    // --- Connected Accounts Actions ---
    // (Google/LinkedIn link-unlink actions removed with the hidden UI — see git history)

    handleVerifyTelegramOtp: async () => {
        const { telegramConnect, user } = get();
        set(state => ({ telegramConnect: { ...state.telegramConnect, error: '' } }));

        if (!telegramConnect.username) {
            set(state => ({ telegramConnect: { ...state.telegramConnect, error: 'Username is required' } }));
            return;
        }
        if (!telegramConnect.otp) {
            set(state => ({ telegramConnect: { ...state.telegramConnect, error: 'OTP is required' } }));
            return;
        }

        try {
            // The account being connected is the one the token names — the
            // backend stopped reading `currentEmail` from the body (2026-09-02).
            const response = await fetch(`${API_URL}/api/connect-telegram`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders('user') },
                body: JSON.stringify({
                    telegramUsername: telegramConnect.username,
                    otp: telegramConnect.otp,
                }),
            });
            if (handleAuthFailure(response)) return;
            const data = await response.json();

            if (response.ok && data.success) {
                const updatedUser = { ...user, ...data.user, merged: data.merged };
                get().setUser(updatedUser);
                get().closeModal('telegramConnect');
                set({ successMessage: data.message || 'Telegram connected successfully!' });
                get().openModal('success');
                window.location.reload();
            } else if (data.requiresMerge) {
                get().closeModal('telegramConnect');
                set({ pendingMergeProfile: data.existingProfile });
                get().openModal('mergeConfirm');
            } else {
                set(state => ({
                    telegramConnect: { ...state.telegramConnect, error: data.message || 'Verification failed' }
                }));
            }
        } catch (error) {
            set(state => ({
                telegramConnect: { ...state.telegramConnect, error: 'An error occurred. Please try again.' }
            }));
        }
    },

    confirmMergeAccounts: async () => {
        const { telegramConnect, user } = get();
        get().closeModal('mergeConfirm');

        try {
            const response = await fetch(`${API_URL}/api/connect-telegram`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders('user') },
                body: JSON.stringify({
                    telegramUsername: telegramConnect.username,
                    confirmMerge: true
                }),
            });
            const data = await response.json();

            // A stale merge ticket is also a 401, but it means "verify the code
            // again", not "your session is gone" — don't log the user out for it.
            if (data?.error_code === 'merge_ticket_required') {
                set(state => ({
                    telegramConnect: { ...state.telegramConnect, otp: '', error: data.message }
                }));
                get().openModal('telegramConnect');
                return;
            }
            if (handleAuthFailure(response)) return;

            if (response.ok && data.success) {
                const updatedUser = { ...user, ...data.user, merged: true };
                get().setUser(updatedUser);
                set({ successMessage: 'Accounts merged successfully!' });
                get().openModal('success');
                window.location.reload();
            }
        } catch (error) {
            // silent fail
        }
    },

    confirmDisconnectTelegram: async () => {
        const { user } = get();
        get().closeModal('disconnectTelegram');

        try {
            const response = await fetch(`${API_URL}/api/disconnect-telegram`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({}),
            });
            if (handleAuthFailure(response)) return;
            const data = await response.json();

            if (response.ok && data.success) {
                const updatedUser = { ...user, username: null, telegramConnected: false, tgId: null };
                get().setUser(updatedUser);
                set({ successMessage: 'Telegram disconnected successfully' });
                get().openModal('success');
            }
        } catch (error) {
            // silent fail
        }
    },

    // --- Account Deletion (30-day freeze) ---

    // Returns true on success. Session stays intact — the user may cancel
    // during the freeze window, so no logout / localStorage changes here.
    deleteAccount: async () => {
        get().closeModal('deleteAccount');

        try {
            const response = await fetch(`${API_URL}/api/account/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders('user') },
                body: JSON.stringify({}),
            });
            if (handleAuthFailure(response)) return false;
            const data = await response.json();

            if (response.ok && data.success) {
                const frozen = {
                    deletionScheduledAt: data.deletionScheduledAt,
                    notificationsPaused: true,
                    nextWeekStatus: 'Passive',
                };
                set(state => ({
                    formData: { ...state.formData, ...frozen },
                    initialFormData: state.initialFormData
                        ? { ...state.initialFormData, ...frozen }
                        : state.initialFormData,
                }));
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    },

    // Returns true on success.
    cancelDeletion: async () => {

        try {
            const response = await fetch(`${API_URL}/api/account/cancel-deletion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders('user') },
                body: JSON.stringify({}),
            });
            if (handleAuthFailure(response)) return false;
            const data = await response.json();

            if (response.ok && data.success) {
                const restored = { deletionScheduledAt: null, notificationsPaused: false };
                set(state => ({
                    formData: { ...state.formData, ...restored },
                    initialFormData: state.initialFormData
                        ? { ...state.initialFormData, ...restored }
                        : state.initialFormData,
                }));
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    },

}));

// Same reason as in App.js: after a 401 the store must forget the dead user,
// otherwise setUser/mergeUser would persist it again.
if (typeof window !== 'undefined') {
    window.addEventListener(SESSION_RESET_EVENT, () => {
        useDashboardStore.setState({ user: null });
    });
}

export default useDashboardStore;
